/**
 * Vendor Detail API Layer
 *
 * Provides API functions and React Query hooks for fetching vendor details.
 * Includes runtime validation with Zod schemas.
 */

import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { get } from '@/infra/rest';
import { parseVendorDetailResponse, type VendorDetailResponse } from '../types';

// ============================================================================
// Query Key Factory
// ============================================================================

/**
 * Query key factory for vendor detail queries.
 * Following React Query best practices for structured query keys.
 *
 * @example
 * // Get all vendor detail keys (for invalidation)
 * queryClient.invalidateQueries({ queryKey: vendorDetailKeys.all });
 *
 * // Get specific vendor detail key
 * const queryKey = vendorDetailKeys.detail('Acme Corp');
 */
export const vendorDetailKeys = {
	/** Base key for all vendor detail queries */
	all: ['vendor-detail'] as const,

	/** Key for a specific vendor's details */
	detail: (vendorName: string) => [...vendorDetailKeys.all, vendorName] as const,
};

// ============================================================================
// Query Configuration
// ============================================================================

/**
 * Default configuration for vendor detail queries.
 * These values can be overridden when using the hook.
 */
export const VENDOR_DETAIL_QUERY_CONFIG = {
	/** Time in ms before data is considered stale (30 seconds) */
	staleTime: 30 * 1000,

	/** Time in ms to keep unused data in cache (5 minutes) */
	gcTime: 5 * 60 * 1000,

	/** Number of retry attempts on failure */
	retry: 3,
};

// ============================================================================
// API Function
// ============================================================================

/**
 * Fetches vendor detail data from the API.
 *
 * @param vendorName - The name of the vendor to fetch details for
 * @returns Promise resolving to validated vendor detail response
 * @throws {ZodError} If the response fails validation
 * @throws {Error} If the API request fails (404, 500, network error)
 *
 * @example
 * try {
 *   const data = await getVendorDetail('Acme Corporation');
 *   console.log(data.vendor_data.vendor_name);
 * } catch (error) {
 *   if (error instanceof ZodError) {
 *     console.error('Invalid response:', error.issues);
 *   }
 * }
 */
export const getVendorDetail = async (vendorName: string): Promise<VendorDetailResponse> => {
	// Encode vendor name for URL (handles special characters like &, /, ', etc.)
	const encodedVendorName = encodeURIComponent(vendorName);

	const response = await get<VendorDetailResponse>({
		url: `/v0/tpra/vendors/${encodedVendorName}/details`,
	});

	// Validate and parse the response with Zod schema
	return parseVendorDetailResponse(response);
};

// ============================================================================
// React Query Hook
// ============================================================================

/**
 * React Query hook for fetching vendor detail data.
 *
 * Provides automatic caching, loading states, error handling, and refetching.
 *
 * @param vendorName - The name of the vendor to fetch details for
 * @param options - Optional React Query options to override defaults
 * @returns React Query result with vendor detail data
 *
 * @example
 * function VendorDetailPage({ vendorName }: { vendorName: string }) {
 *   const { data, isLoading, isError, error, refetch } = useVendorDetailQuery(vendorName);
 *
 *   if (isLoading) return <VendorDetailSkeleton />;
 *   if (isError) return <VendorDetailError error={error} onRetry={refetch} />;
 *
 *   return <VendorDetailContent data={data.vendor_data} />;
 * }
 */
export const useVendorDetailQuery = (
	vendorName: string,
	options?: Omit<
		UseQueryOptions<VendorDetailResponse, Error>,
		'queryKey' | 'queryFn'
	>
) => {
	return useQuery<VendorDetailResponse, Error>({
		queryKey: vendorDetailKeys.detail(vendorName),
		queryFn: () => getVendorDetail(vendorName),

		// Default configuration
		staleTime: VENDOR_DETAIL_QUERY_CONFIG.staleTime,
		gcTime: VENDOR_DETAIL_QUERY_CONFIG.gcTime,
		retry: VENDOR_DETAIL_QUERY_CONFIG.retry,

		// Refetch behavior
		refetchOnWindowFocus: true,
		refetchOnMount: true,
		refetchOnReconnect: true,

		// Only enable if vendor name is provided
		enabled: Boolean(vendorName),

		// Allow user overrides
		...options,
	});
};
